import { useMemo, useRef, useState, useLayoutEffect, type ReactNode } from 'react';
import { fmtLocalDate } from '../../../utils/datetime';
import { LjChart } from './ControlChartSvg';
import {
  THEMES, RULE_TEXT, sig, stamp, valueLabel, isMicro, evaluateRules, dispositionOf,
  type Field, type QcRow, type Signature, type Stat, type TestDef,
} from './controlChartData';

// ─────────────────────────────────────────────────────────────────────────────
// QA/QC → Control Charts → report preview
//
// A DOCUMENT, not a dialog: the QC record that gets printed, signed and filed.
// Letter-size sheets on a grey backdrop, the lab letterhead on page one and a
// continuation header after it, a page footer, and REAL pagination — the page
// count in the bar is measured, not guessed.
//
// Pagination is a two-pass render: every block is laid out once in a hidden
// stage at the exact content width, its height measured, then the blocks are
// dealt into pages. Estimating heights instead would drift the moment a
// parameter name wrapped onto a second line, and the page count would be a
// number that looks authoritative and isn't.
// ─────────────────────────────────────────────────────────────────────────────

/** US Letter at 96 dpi plus the sheet padding — the prototype's geometry. */
const RP_H = 1056;
const RP_PAD = 54;
/** Height the two header variants take off the usable page. */
const HEAD_FULL = 104;
const HEAD_CONT = 66;

function Letterhead({ full, test, qcTypes, from, to }: {
  full: boolean; test: TestDef | null; qcTypes: string[]; from: string; to: string;
}) {
  return (
    <div className="cc-rhead">
      <div className="cc-lh">
        <div className="cc-mk">A</div>
        <div className="cc-lhwho">
          <div className="cc-lhnm">Alpaca Analytical Laboratories, Inc.</div>
          {full ? (
            <div className="cc-lhad">
              4820 Bissonnet Street, Suite 210 · Houston, TX 77401<br />
              T (713) 555-0142 · qa@alpaca-labs.example
            </div>
          ) : (
            <div className="cc-lhad">
              Control Chart Report · {test?.test} · {qcTypes.join(', ') || 'all'}
            </div>
          )}
        </div>
        {full ? (
          <div className="cc-accs">
            <b>Accreditations</b>
            NELAP / TNI — T104704215<br />
            A2LA ISO/IEC 17025 — 2841.01<br />
            TCEQ Drinking Water — LAB0142
          </div>
        ) : (
          <div className="cc-accs">
            <b>Continued</b>
            {fmtLocalDate(from)} – {fmtLocalDate(to)}
          </div>
        )}
      </div>
      <div className="cc-rrule" />
    </div>
  );
}

export interface ReportProps {
  test: TestDef | null; rows: QcRow[]; stats: Stat[]; field: Field;
  from: string; to: string; qcTypes: string[]; inst: string[]; anly: string[]; role: string;
  signature: Signature | null; ccid: string;
  onClose: () => void;
}

export function ReportDialog({ test, rows, stats, field, from, to, qcTypes, inst, anly, role,
  signature, ccid, onClose }: ReportProps) {
  const micro = isMicro(test);
  const label = valueLabel(field);

  const model = useMemo(() => stats.map(st => {
    const vals = rows.filter(r => r.p === st.parameter && Number.isFinite(r[field] as number))
      .map((r, i) => ({ i: i + 1, d: r.d, v: r[field] as number, batch: r.batch, anly: r.anly }));
    return { st, vals, viol: evaluateRules(vals, st), ok: !evaluateRules(vals, st).length };
  }), [stats, rows, field]);
  const bad = model.filter(m => !m.ok).length;

  const blocks: ReactNode[] = [];

  // ── title + definition ────────────────────────────────────────────────────
  blocks.push(
    <div key="title">
      <div className="cc-rtitle">
        <h2>Control Chart Report</h2>
        {micro ? (
          <div className="cc-rkind">
            {field === 'growth' ? 'Microbiological sterility — negative-control attribute chart'
              : field === 'logc' ? 'Microbiological accuracy — positive-control individuals chart on log₁₀ count'
                : 'Microbiological precision — duplicate range chart · SM 9020 B'}
          </div>
        ) : null}
        <div className="cc-cn">
          {ccid ? <>Chart ID <b>{ccid}</b> · </> : null}
          Issued <b>{stamp().slice(0, 10)}</b> ·{' '}
          {bad
            ? <><b>{bad}</b> parameter{bad === 1 ? '' : 's'} out of control</>
            : 'All parameters in control'}
        </div>
      </div>
      <div className="cc-rmeta">
        <div className="cc-rblock">
          <h3>Chart definition</h3>
          <div className="cc-rrow"><span>Test</span><b>{test?.test}</b></div>
          <div className="cc-rrow"><span>Method</span><b>{test?.method}</b></div>
          <div className="cc-rrow"><span>Matrix</span><b>{test?.matrix}</b></div>
          <div className="cc-rrow"><span>QC type</span><b>{qcTypes.join(', ') || 'all'}</b></div>
          <div className="cc-rrow"><span>Charted value</span><b>{label}</b></div>
        </div>
        <div className="cc-rblock">
          <h3>Period &amp; data set</h3>
          <div className="cc-rrow"><span>From</span><b>{fmtLocalDate(from)}</b></div>
          <div className="cc-rrow"><span>To</span><b>{fmtLocalDate(to)}</b></div>
          <div className="cc-rrow"><span>Parameters</span><b>{model.length}</b></div>
          <div className="cc-rrow"><span>QC results</span><b>{rows.length.toLocaleString()}</b></div>
          <div className="cc-rrow"><span>Instrument</span><b>{inst.join(', ') || 'all'}</b></div>
          <div className="cc-rrow"><span>Analyst</span><b>{anly.join(', ') || 'all'}</b></div>
          {role ? <div className="cc-rrow"><span>QC role</span><b>{role}</b></div> : null}
        </div>
      </div>
    </div>,
  );

  // ── evaluation summary ────────────────────────────────────────────────────
  // Each chart kind gets its OWN columns: printing Mean/SD/UCL/UWL for a
  // sterility blank would fill the row with dashes and imply the numbers exist.
  blocks.push(
    <div key="eval">
      <div className="cc-rsec">
        <h3>Evaluation Summary</h3>
        <span className="cc-rnote">
          {micro ? 'Mean range R̄ · criterion 3.27 R̄ (D₄, n = 2) · lower limit 0 · SM 9020 B'
            : 'Mean ± 2SD warning, ± 3SD control · sample SD (n − 1) · SM 1020 B'}
        </span>
      </div>
      <table className="cc-rt">
        <thead>
          {field === 'growth'
            ? (
              <tr><th>Parameter</th><th className="n">Blanks</th><th className="n">Passed</th>
                <th className="n">With growth</th><th className="n">Rate</th><th>Signals</th>
                <th className="c">Verdict</th></tr>
            ) : field === 'logc' ? (
              <tr><th>Parameter</th><th className="n">Mean log₁₀</th><th className="n">Mean count</th>
                <th className="n">SD</th><th className="n">UCL</th><th className="n">LCL</th>
                <th className="n">n</th><th>Rules</th><th className="c">Verdict</th></tr>
            ) : micro ? (
              <tr><th>Parameter</th><th className="n">Mean range R̄</th><th className="n">Criterion 3.27 R̄</th>
                <th className="n">Largest R</th><th className="n">Pairs</th><th>Signals</th>
                <th className="c">Verdict</th></tr>
            ) : (
              <tr><th>Parameter</th><th className="n">Mean</th><th className="n">SD</th><th className="n">UCL</th>
                <th className="n">UWL</th><th className="n">LWL</th><th className="n">LCL</th>
                <th className="n">n</th><th>Rules</th><th className="c">Verdict</th></tr>
            )}
        </thead>
        <tbody>
          {model.map(m => (
            <tr key={m.st.parameter}>
              <td className="an">{m.st.parameter}</td>
              {m.st.kind === 'attribute' ? (
                <>
                  <td className="n">{m.st.count}</td><td className="n">{m.st.pass}</td>
                  <td className="n">{m.st.fail}</td>
                  <td className="n">{((m.st.rate ?? 0) * 100).toFixed(1)} %</td>
                </>
              ) : m.st.kind === 'individuals' ? (
                <>
                  <td className="n">{sig(m.st.mean)}</td><td className="n">{m.st.meanCount}</td>
                  <td className="n">{sig(m.st.std)}</td><td className="n">{sig(m.st.ucl)}</td>
                  <td className="n">{sig(m.st.lcl)}</td><td className="n">{m.st.count}</td>
                </>
              ) : m.st.kind === 'range' ? (
                <>
                  <td className="n">{sig(m.st.mean)}</td><td className="n">{sig(m.st.ucl)}</td>
                  <td className="n">{sig(m.st.maxR)}</td><td className="n">{m.st.count}</td>
                </>
              ) : (
                <>
                  <td className="n">{sig(m.st.mean)}</td><td className="n">{sig(m.st.std)}</td>
                  <td className="n">{sig(m.st.ucl)}</td><td className="n">{sig(m.st.uwl)}</td>
                  <td className="n">{sig(m.st.lwl)}</td><td className="n">{sig(m.st.lcl)}</td>
                  <td className="n">{m.st.count}</td>
                </>
              )}
              <td>
                {m.viol.length
                  ? m.viol.map(v => <span key={v.rule} className="cc-rcode">{v.rule}</span>)
                  : '—'}
              </td>
              <td className={`c ${m.ok ? 'cc-rok' : 'cc-rbad'}`}>
                {m.ok ? 'In Control' : 'Out of Control'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>,
  );

  // ── narrative ─────────────────────────────────────────────────────────────
  // The two chart kinds are described by their OWN basis. Bolting the micro
  // sentence onto the chemistry preamble produced "mean ± 2SD … per Standard
  // Methods 9020 B" — two methods in one sentence and neither of them right.
  const nar: ReactNode[] = [
    <p key="n1">
      This report covers <b>{model.length}</b> control chart{model.length === 1 ? '' : 's'} for{' '}
      {test?.test} ({test?.method}), {qcTypes.join(', ') || 'all'}{' '}
      {micro ? 'duplicate precision' : label.toLowerCase()}, over {fmtLocalDate(from)} to{' '}
      {fmtLocalDate(to)} — <b>{rows.length.toLocaleString()}</b> QC results.{' '}
      {micro
        ? <>The plotted value is the range R = |log₁₀ C₁ − log₁₀ C₂| of each duplicate pair, centred on
          the mean range R̄ with a single upper criterion of 3.27 R̄ (D₄ for a subgroup of two) and a lower
          limit of zero, per Standard Methods 9020 B. Counts are log-transformed because microbial counts
          are log-normal.</>
        : <>Limits are the mean ± 2SD (warning) and ± 3SD (control), computed with the sample standard
          deviation (n − 1) per Standard Methods 1020 B.</>}
    </p>,
  ];
  if (!bad) {
    nar.push(
      <p key="n2">
        {micro
          ? 'Every duplicate pair fell within the precision criterion over the period. No excursion was observed and the existing criterion is retained.'
          : 'Every parameter satisfied all six control rules over the period. No excursion was observed and the existing limits are retained.'}
      </p>,
    );
  } else {
    nar.push(
      <p key="n2">
        <b>{bad}</b> of {model.length} parameters signalled:{' '}
        {model.filter(m => !m.ok).map((m, mi, arr) => (
          <span key={m.st.parameter}>
            <b>{m.st.parameter}</b> —{' '}
            {m.viol.map((v, vi) => (
              <span key={v.rule}>
                <span className="cc-rcode">{v.rule}</span> at point {v.p.i} ({fmtLocalDate(v.p.d)}, {v.p.v})
                {vi < m.viol.length - 1 ? ', ' : ''}
              </span>
            ))}
            {mi < arr.length - 1 ? '; ' : ''}
          </span>
        ))}. Each is dispositioned below.
      </p>,
      <p key="n3">
        {micro
          ? 'A range beyond the criterion is a failure of PRECISION, not of accuracy — the two counts disagreed by more than duplicate analysis should. It is resolved by repeating the duplicate and reviewing dilution and plating technique, not by widening the criterion.'
          : 'A rule signal is not by itself a rejected batch: 1-3s and R-4s are rejection rules, while the run rules (2-2s, 4-1s, 10x, 7T) indicate systematic error that must be investigated and may be resolved by resetting limits once the cause is understood and documented.'}
      </p>,
    );
  }
  blocks.push(
    <div key="narr">
      <div className="cc-rsec"><h3>Narrative</h3></div>
      <div className="cc-rprose">{nar}</div>
    </div>,
  );

  // ── excursions ────────────────────────────────────────────────────────────
  if (bad) {
    blocks.push(
      <div key="exc">
        <div className="cc-rsec"><h3>Excursions &amp; Disposition</h3></div>
        <table className="cc-rt">
          <thead>
            <tr><th>Parameter</th><th>Rule</th><th className="n">Point</th><th>Date</th>
              <th className="n">Value</th><th className="n">z</th><th>QC Batch</th><th>Analyst</th>
              <th>Disposition</th></tr>
          </thead>
          <tbody>
            {model.flatMap(m => m.viol.map(v => {
              const z = m.st.std ? (v.p.v - m.st.mean!) / m.st.std : null;
              return (
                <tr key={`${m.st.parameter}-${v.rule}`}>
                  <td className="an">{m.st.parameter}</td>
                  <td><span className="cc-rcode">{v.rule}</span></td>
                  <td className="n">{v.p.i}</td>
                  <td>{fmtLocalDate(v.p.d)}</td>
                  <td className="n">{v.p.v}</td>
                  {/* A range and an attribute chart have no SD, so there is no
                      z-score to state — a number here would be invented. */}
                  <td className="n">{z === null ? '—' : `${z > 0 ? '+' : ''}${z.toFixed(2)}`}</td>
                  <td className="cc-mono">{v.p.batch || '—'}</td>
                  <td>{v.p.anly || '—'}</td>
                  <td>{dispositionOf(v.rule)}</td>
                </tr>
              );
            }))}
          </tbody>
        </table>
      </div>,
    );
  }

  // ── the charts themselves ─────────────────────────────────────────────────
  // A QC record that states "3 rules fired" without showing the series is an
  // assertion, not evidence. Drawn in the PRINT theme: monochrome and
  // dash-separated, because colour photocopies to identical greys.
  blocks.push(<div key="chartsec"><div className="cc-rsec"><h3>Control Charts</h3></div></div>);
  model.forEach(m => {
    blocks.push(
      <div key={`chart-${m.st.parameter}`} className="cc-rchart">
        <div className="cc-rchart-t">
          {m.st.parameter}
          <span className={`cc-pill ${m.ok ? 'ok' : 'bad'}`}>
            {m.ok ? 'In control' : m.viol.map(v => v.rule).join(', ')}
          </span>
        </div>
        <LjChart pts={m.vals.map(v => ({ v: v.v, d: v.d }))} st={m.st} field={field} theme={THEMES.print!} />
      </div>,
    );
  });

  // ── rules + basis + authorisation ─────────────────────────────────────────
  blocks.push(
    <div key="rules">
      <div className="cc-rsec"><h3>Control Rules Applied</h3></div>
      <table className="cc-rt">
        <tbody>
          {RULE_TEXT.map(([r, txt]) => (
            <tr key={r}>
              <td className="an" style={{ whiteSpace: 'nowrap' }}>{r}</td>
              <td>{txt}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>,
  );

  blocks.push(
    <div key="basis">
      <div className="cc-rsec"><h3>Basis</h3></div>
      <div className="cc-rprose">
        <p>
          <b>Limits.</b>{' '}
          {field === 'rng'
            ? 'Standard Methods 9020 B §9c — the mean range R̄ with the upper criterion 3.27 R̄ (factor D₄ for a subgroup of two); lower limit D₃ = 0. No warning band is defined for a range chart.'
            : field === 'growth'
              ? 'The acceptance criterion is zero growth. A negative control is an attribute, so no mean, standard deviation or control limit is computed.'
              : `Computed from the data in the selected window using the sample standard deviation (n − 1) per Standard Methods 1020 B. Warning limits at mean ± 2SD, control limits at mean ± 3SD${field === 'rpd' ? '; lower limits floored at zero because an RPD cannot be negative' : ''}${field === 'logc' ? ', computed on the log₁₀ of the count because counts are log-normal' : ''}.`}
        </p>
        <p>
          <b>Rules.</b>{' '}
          {field === 'rng' || field === 'growth'
            ? 'The Westgard multirule set is NOT applied to this chart type — those rules read a point’s distance from a mean in units of SD and assume the series is free to wander either side of it, which is true of neither a range nor an attribute.'
            : 'Westgard multirule set (1-3s, 2-2s, R-4s, 4-1s, 10x, 7T), evaluated over the plotted series in date order. Each rule is reported once, at its earliest occurrence.'}
        </p>
        <p>
          <b>Data source.</b> QC results for {test?.test} ({test?.method}),{' '}
          {qcTypes.join(', ') || 'all QC types'}, {fmtLocalDate(from)} – {fmtLocalDate(to)}.
        </p>
      </div>
    </div>,
  );

  blocks.push(
    <div key="auth">
      <div className="cc-rsec"><h3>Authorisation</h3></div>
      {signature ? (
        <div className="cc-rsign">
          <div className="cc-sgwho">
            <span className="cc-sgmark">{signature.by.slice(0, 1)}</span>
            <div>
              <div className="cc-sgnm">{signature.by}</div>
              <div className="cc-sgti">{signature.role} · {signature.uid}</div>
            </div>
          </div>
          <div className="cc-sgm2">
            Signed <b>{signature.at}</b><br />
            {signature.meaning}<br />
            Fingerprint <b>{signature.hash}</b>
          </div>
        </div>
      ) : (
        <div className="cc-rsign unsigned">
          <div className="cc-sgwho">
            <div>
              <div className="cc-sgnm">Not yet signed</div>
              <div className="cc-sgti">This data set has not been electronically signed.</div>
            </div>
          </div>
        </div>
      )}
    </div>,
  );

  // ── pagination: measure, then deal ────────────────────────────────────────
  const stage = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<number[][] | null>(null);

  useLayoutEffect(() => {
    if (pages) return;
    const el = stage.current;
    if (!el) return;
    const hs = [...el.children].map(c => (c as HTMLElement).offsetHeight);
    const out: number[][] = [];
    let cur: number[] = [];
    let used = 0;
    hs.forEach((h, i) => {
      const cap = RP_H - RP_PAD * 2 - (out.length === 0 ? HEAD_FULL : HEAD_CONT);
      // A block taller than a whole page cannot be split at this granularity, so
      // it takes a page of its own and is allowed to run long — dropping it, or
      // silently clipping it, would lose evidence from a signed record.
      if (cur.length && used + h > cap) { out.push(cur); cur = []; used = 0; }
      cur.push(i);
      used += h;
    });
    if (cur.length) out.push(cur);
    setPages(out);
  }, [pages]);

  const pageCount = pages?.length ?? 0;

  return (
    <div className="cc-preview" role="dialog" aria-modal="true" aria-label="Control Chart Report">
      <div className="cc-pvbar">
        <div className="cc-pvttl">
          <b>Control Chart Report</b>
          <span className="cc-pvsub">
            {test?.test} · {qcTypes.join(', ') || 'all'} · {fmtLocalDate(from)} – {fmtLocalDate(to)}
            {pages
              ? <> · {pageCount} page{pageCount === 1 ? '' : 's'}
                {bad ? ` · ${bad} out of control` : ' · all in control'}</>
              : <> · paginating…</>}
          </span>
        </div>
        <span className="cc-sp" />
        <button type="button" className="ca-btn" onClick={onClose}>Close</button>
        <button type="button" className="ca-btn ca-btn-solid" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
      </div>

      <div className="cc-pvsheets">
        {/* Measuring pass — same width as the sheet content, so a line that wraps
            here wraps there too. Removed from the DOM once the deal is done. */}
        {!pages ? <div className="cc-rstage" ref={stage} aria-hidden="true">{blocks}</div> : null}

        {pages?.map((idxs, pi) => (
          <div className="cc-rpage" key={pi}>
            <div className="cc-rbody">
              <Letterhead full={pi === 0} test={test} qcTypes={qcTypes} from={from} to={to} />
              {idxs.map(i => blocks[i])}
            </div>
            <div className="cc-rfoot">
              <span>Control Chart Report{ccid ? ` · Chart ${ccid}` : ''} · {test?.test}</span>
              <span className="cc-sp" />
              <span>Page {pi + 1} of {pageCount}{pi === pageCount - 1 ? ' · End of report' : ''}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
